import { createContext, useContext, useState } from "react";
import { useModalContext } from "./ModalContext";
import storageService from "../services/storageService";
import axiosInstance, { handleApiError } from "../services/axiosService";
import { toastSuccess } from "../utils/toastUtils";

const DBConnectorsContext = createContext();
const useDBConnectorsContext = () => useContext(DBConnectorsContext);

const DBConnectorContext = ({ children }) => {
  const [explorerData, setExplorerData] = useState([
    {
      id: "connectors",
      name: "Connectors",
      parentData: "connectors",
      isFolder: true,
      items: [],
      can_delete: false,
      can_insert: true,
      isOpen: true,
      insert_type: "popup",
      class_name: "",
      disabled: false,
      folderInfo: {}
    }
  ]);

  const [selectedConnector, setSelectedConnector] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [tables, setTables] = useState([]);
  const [views, setViews] = useState([]);
  const [edges, setEdges] = useState([]);
  const [modifiedAt, setModifiedAt] = useState(null);
  const [showPopup, setShowPopup] = useState(false);

  const { getUserConfirmationForDelete } = useModalContext();

  const splitAndSetSchema = (schema) => {
    if (!schema || !Array.isArray(schema.nodes)) return;

    const allNodes = schema?.nodes || [];
    const onlyTables = allNodes.filter((node) => node?.table_type !== "VIEW");
    const onlyViews = allNodes.filter((node) => node?.table_type === "VIEW");

    setTables(onlyTables);
    setViews(onlyViews);
    setEdges(schema?.edges);
    setModifiedAt(schema.modified_at);
  };

  const fetchConnectors = async () => {
    try {
      const { data } = await axiosInstance.get("/dbconnector");

      const transformedData = data.map((item) => ({
        id: item.id,
        name: item?.name || "",
        parentData: "connectors",
        isFolder: false,
        items: [],
        can_delete: true,
        can_edit: false,
        type: item?.database_type || "",
        fileInfo: {}
      }));

      setExplorerData((prev) => [
        {
          ...prev[0],
          items: transformedData
        }
      ]);
    } catch (error) {
      handleApiError(error);
    }
  };

  const getSchema = async (selectedConnectorId, reload = false) => {
    try {
      setIsLoading(true);
      setTables([]);
      setViews([]);
      setEdges([]);

      const response = await axiosInstance.get(`/schema${reload ? "/reload" : ""}/connector/${selectedConnectorId}`);
      const receivedSchema = reload ? response?.schema : response;

      splitAndSetSchema(receivedSchema);

      if (reload) toastSuccess(response.message);
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setIsLoading(true);
      const response = await axiosInstance.patch(`/schema/${selectedConnector.id}/connector/${selectedConnector.id}`, {
        edges,
        nodes: [...tables, ...views]
      });
      toastSuccess(response.message);
      splitAndSetSchema(response.schema);
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (data) => {
    const userConfirmed = await getUserConfirmationForDelete(data.name);
    if (!userConfirmed) return;

    try {
      const response = await axiosInstance.delete(`/dbconnector/${data.id}`);
      toastSuccess(response.message);
      setExplorerData((prev) => [
        {
          ...prev[0],
          items: prev[0].items.filter((item) => item.id !== data.id)
        }
      ]);

      if (selectedConnector?.id === data.id) {
        setSelectedConnector(null);
      }

      storageService.remove(`reactFlowViewport_${selectedConnector?.id}`);
      storageService.remove(`reactFlowViewport_${selectedConnector?.id}_view`);
    } catch (error) {
      handleApiError(error);
    }
  };

  const handleConnectorSelect = (id) => {
    if (selectedConnector?.id === id) return;

    const currentConnector = explorerData[0].items.find((item) => item.id === id);
    setSelectedConnector(currentConnector || null);
    setTables([]);
    setViews([]);
    setEdges([]);
  };

  const togglePopup = (state) => {
    setShowPopup(state);
  };

  return (
    <DBConnectorsContext.Provider
      value={{
        explorerData,
        selectedConnector,
        isLoading,
        tables,
        views,
        edges,
        modifiedAt,
        showPopup,
        setExplorerData,
        setSelectedConnector,
        setShowPopup,
        fetchConnectors,
        getSchema,
        handleSave,
        handleDelete,
        handleConnectorSelect,
        togglePopup,
        splitAndSetSchema
      }}
    >
      {children}
    </DBConnectorsContext.Provider>
  );
};

export { DBConnectorContext, useDBConnectorsContext };
